import { useState, useCallback, useEffect, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { AuthContext, type AuthContextType, type AvailableTenant } from "@/hooks/use-auth"
import { api } from "@/services/api"
import type { AvailableTenantApi, User } from "@/types/api"
import { isTenantClosedRefusal, markTenantClosed, resetTenantClosed, tenantClosureOf } from "@/lib/tenant-closed"

interface AuthProviderProps {
  children: ReactNode
}

function getInitialUser(): User | null {
  // With cookie auth, tokens are in HttpOnly cookies (not accessible via JS)
  // We use the stored user info to determine initial auth state
  const storedUser = localStorage.getItem("user")
  if (storedUser && storedUser !== "undefined") {
    return normalizeUser(JSON.parse(storedUser) as User)
  }
  return null
}

/**
 * Boundary hardening for the permission-aware UI : a `user` object persisted in
 * localStorage before the permissions release lacks the `permissions` array.
 * Reading it as-is would make `usePermissions()` throw on `.includes`. We
 * default a missing/legacy value to `[]` at the entry point — the very next
 * `/auth/me` (fired on mount) refreshes it with the authoritative server list,
 * and admin roles keep full access via their role name regardless.
 */
function normalizeUser(raw: User): User {
  return Array.isArray(raw.permissions) ? raw : { ...raw, permissions: [] }
}

function getInitialLoading(): boolean {
  // If user info exists in localStorage, verify auth on mount.
  // If we're on a `/demo/*` path with no user yet, also flip into loading
  // so ProtectedRoute waits for the public-session bootstrap below instead
  // of redirecting to /login.
  if (localStorage.getItem("user")) {
    return true
  }
  return isDemoPath()
}

function isDemoPath(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  const path = window.location.pathname
  return path === "/demo" || path.startsWith("/demo/")
}

/**
 * Persiste le slug actif et la liste des tenants disponibles renvoyés par le
 * serveur, et retourne la liste normalisée pour le state React. La source de
 * vérité est la réponse `/auth/me` (ou `/auth/login`) — on n'invente plus la
 * liste depuis `user` quand le serveur ne nous l'a pas fournie.
 *
 * Edge case (read-after-write lag) : sur staging CNPG, le `POST /organizations`
 * peut être suivi d'un `GET /auth/me` qui lit une replica encore en retard et
 * renvoie l'ancienne liste (1 tenant). Si la liste serveur est plus courte que
 * la liste persistée localement, on garde l'union (la liste locale gagne) pour
 * éviter de faire disparaître l'OrgSwitcher pendant la fenêtre de réplication.
 */
function persistTenantsFromServer(user: User, serverTenants: AvailableTenantApi[]): AvailableTenant[] {
  const slug = user.tenant_code || "default"
  localStorage.setItem("lastOrgSlug", slug)
  const fromServer: AvailableTenant[] = serverTenants.map((t) => ({
    id: t.id,
    name: t.name,
    slug: t.slug,
    role: (t.role_code || user.role).toLowerCase(),
    created_at: t.created_at,
    closed: t.closed,
  }))

  // Merge with locally persisted list to survive read-after-write lag.
  const stored = localStorage.getItem("available_tenants")
  const local: AvailableTenant[] = stored ? (JSON.parse(stored) as AvailableTenant[]) : []
  const byId = new Map<string, AvailableTenant>()
  for (const t of local) byId.set(t.id, t)
  for (const t of fromServer) byId.set(t.id, t) // server wins on metadata
  const merged = Array.from(byId.values())

  localStorage.setItem("available_tenants", JSON.stringify(merged))
  return merged
}

function getInitialTenants(): AvailableTenant[] {
  const stored = localStorage.getItem("available_tenants")
  if (stored) {
    return JSON.parse(stored) as AvailableTenant[]
  }
  return []
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(getInitialUser)
  const [isLoading, setIsLoading] = useState(getInitialLoading)
  const [availableTenants, setAvailableTenants] = useState<AvailableTenant[]>(getInitialTenants)

  const isAuthenticated = user !== null

  // Verify auth on mount - cookies are sent automatically via withCredentials.
  // The axios response interceptor (services/api.ts) already handles 401 by
  // refreshing the access token transparently and queuing concurrent requests,
  // so we just call getCurrentUser once and trust the interceptor to retry.
  useEffect(() => {
    const storedUser = localStorage.getItem("user")
    if (storedUser) {
      api.getCurrentUser()
        .then((response) => {
          setUser(response.user)
          localStorage.setItem("user", JSON.stringify(response.user))
          setAvailableTenants(persistTenantsFromServer(response.user, response.available_tenants))
          setIsLoading(false)
        })
        .catch((error: unknown) => {
          // Organisme fermé : la session reste, ProtectedRoute affiche la page datée.
          if (isTenantClosedRefusal(error)) {
            markTenantClosed(tenantClosureOf(error))
            setIsLoading(false)
            return
          }
          api.clearToken()
          setUser(null)
          setIsLoading(false)
        })
    } else if (isDemoPath()) {
      // Anonymous visitor (or screenshot tool) landed on /demo/* — auto-fetch
      // a public demo session so they can browse the read-only demo without
      // signing up.
      api.get<{ user: User; token_type: string; expires_in: number; available_tenants: AvailableTenantApi[] }>("/demo/public-session")
        .then((response) => {
          const demoUser = normalizeUser(response.user)
          setUser(demoUser)
          localStorage.setItem("user", JSON.stringify(demoUser))
          setAvailableTenants(persistTenantsFromServer(demoUser, response.available_tenants ?? []))
          setIsLoading(false)
        })
        .catch(() => {
          setUser(null)
          setIsLoading(false)
        })
    }
  }, [])

  const login = useCallback((email: string, password: string): Promise<void> => {
    return api.login({ email, password })
      .then((response) => {
        resetTenantClosed()
        setUser(response.user)
        setAvailableTenants(persistTenantsFromServer(response.user, response.available_tenants))
      })
  }, [])

  const logout = useCallback((): Promise<void> => {
    return api.logout()
      .then(() => {
        resetTenantClosed()
        setUser(null)
        setAvailableTenants([])
        localStorage.removeItem("available_tenants")
        localStorage.removeItem("lastOrgSlug")
      })
      .catch(() => {
        resetTenantClosed()
        setUser(null)
        setAvailableTenants([])
        localStorage.removeItem("available_tenants")
        localStorage.removeItem("lastOrgSlug")
      })
  }, [])

  // The role, plan and permissions belong to the membership, not to the person:
  // the same email can be ADMIN of its own organisation and VIEWER of the demo.
  // Every permission gate reads `user`, so it is replaced by the target
  // membership; a stale one hides the admin actions of the new organisation.
  const switchTenant = useCallback((tenant: AvailableTenant): Promise<void> => {
    return api.switchTenant(tenant.id)
      .then((response) => {
        setUser(response.user)
        setAvailableTenants(persistTenantsFromServer(response.user, response.available_tenants))
        resetTenantClosed()
        queryClient.clear()
        navigate(`/${tenant.slug}/dashboard`)
      })
  }, [queryClient, navigate])

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    availableTenants,
    login,
    logout,
    switchTenant,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
